# GameContainer.vue
<script setup>
import { ref, onMounted, computed, inject } from 'vue'
import { fetchWrapper } from '@/helpers/fetch-wrapper'
import { useProfileStore } from '@/stores/profile.store'
import MultipleChoiceGame from '@/components/games/MultipleChoiceGame.vue'
import MatchingGame from '@/components/games/MatchingGame.vue'
import AudioChoiceGame from '@/components/games/AudioChoiceGame.vue'
import MiniChat from '@/components/MiniChat.vue'
import ExampleSentences from '@/components/ExampleSentences.vue'
import successMp3 from '@/assets/sounds/success.mp3'
import wrongMp3 from '@/assets/sounds/wrong.mp3'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { IonButtons,
    IonButton,
    IonModal,
    IonHeader,
    IonContent,
    IonToolbar,
    IonTitle,
  } from '@ionic/vue'
import ReportConceptModal from '@/components/ReportConceptModal.vue'
import FreeTranslationGame from '@/components/games/FreeTranslationGame.vue'
import IntroduceConceptGame from '@/components/games/IntroduceConceptGame.vue'
import SentenceBuilderGame from '@/components/games/SentenceBuilderGame.vue'

const emit = defineEmits(['topic-completed', 'incrementGameStepsShown'])
const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const profileStore = useProfileStore()
const audiomanager = inject('audiomanager')

const props = defineProps({
  chatID: {
    type: String,
    required: false
  },
  topicID: {
    type: String,
    required: false
  },
  flashcards: {
    type: Boolean,
    default: false
  },
  quiz_sessionID: {
    type: String,
    required: false
  },
  hideProgress: {
    type: Boolean,
    default: false
  },
  compact: {
    type: Boolean,
    default: false
  },
  allowSkip: {
    type: Boolean,
    default: true
  },
  mode: {
    type: String,
    default: 'normal' // 'normal' or 'registration' or 'practice'
  }
})

function showEncouragement(message) {
  showEncouragementMessage.value = message
  encouragementClass.value = 'fade-in'
  setTimeout(() => {
    // encouragementClass.value = 'fade-out'
    setTimeout(() => {
      // showEncouragementMessage.value = ''
      // encouragementClass.value = ''
    }, 1000) // Duration of fade-out
  }, 2000) // Duration to display the message before fading out
}

// State management
const firstGame = ref(true) // Track if this is the first game played
const reloadKey = ref(0)
const notifiedCompleted = ref(false) // Track if the topic has been completed
const isLoading = ref(false)
const currentSession = ref(null)
const nextSession = ref(null)
const gameError = ref(null)
const showResult = ref(false)
const lastResult = ref(null)
const lastAction = ref(null)
const noExercisesAvailable = ref(false)
const miniStreak = ref(0) // how many questions they got right in a row
const showChatModal = ref(false) // New state for chat modal
const showExampleSentencesModal = ref(false) // New state for example sentences modal
const showEncouragementMessage = ref('') // New state for encouragement message
const encouragementClass = ref('') // New state for encouragement message class
const showReportModal = ref(false) // New state for report modal
const hasChattedAfterAnswer = ref(false) // Track if user has chatted after answering
const hasEverChattedInRegistration = ref(false) // Track if user has ever chatted during registration mode
const currentMode = ref(props.mode) // Make mode reactive so we can change it
const currentQuizSessionID = ref(props.quiz_sessionID) // Make quiz session ID reactive
// topicProgress is a float from 0 to 1 that represents how much of the unit has been mastered
const topicProgress = ref(0)
const cardsRemaining = computed(() => {
  if (currentSession.value) {
    return currentSession.value.remaining
  }
  return 0
})
const cardsTotal = computed(() => {
  if (currentSession.value) {
    return currentSession.value.total
  }
  return 0
})
const progressDetails = ref([])
const enable_audio = ref(true)
const enable_speaking = ref(true)
const correctMessage = ref('')
const incorrectMessage = ref('')
const reward_notifications = inject('reward_notifications')

// Computed property to determine if next button should be disabled
const isNextButtonDisabled = computed(() => {
  return currentMode.value === 'registration' && 
         showResult.value && 
         !hasChattedAfterAnswer.value && 
         !hasEverChattedInRegistration.value
})

// Computed property to determine if tooltip should be shown
const shouldShowChatTooltip = computed(() => {
  return currentMode.value === 'registration' && 
         showResult.value && 
         !hasChattedAfterAnswer.value && 
         !hasEverChattedInRegistration.value
})

function updateCorrectMessage() {
  let options = [
    t('curriculum.correct1'),
    t('curriculum.correct2'),
    t('curriculum.correct3'),
  ]
  correctMessage.value = options[Math.floor(Math.random() * options.length)]
}

function updateIncorrectMessage() {
  let options = [
    t('curriculum.incorrect'),
  ]
  incorrectMessage.value = options[Math.floor(Math.random() * options.length)]
}

const isSuperuser = computed(() => profileStore.profile.is_superuser)

async function loadGameState() {
  isLoading.value = true
  try {
    let url = ''
    let response = null
    console.log("hello")
    if (props.flashcards) {
      console.log("just flashcards")
      const params = {
        native_language: profileStore.profile.native_language,
        learning_language: profileStore.profile.learning_language,
        enable_audio: enable_audio.value,
        enable_speaking: enable_speaking.value,
        quiz_sessionID: currentQuizSessionID.value,
        shuffle: true,
        force: firstGame.value,
        supported_games: [
          'multiple-choice',
          'audio-choice',
          'matching',
          'free-translation',
          'free-translation-listening',
          'free-translation-writing',
          'introduce-concept',
          'sentence-builder',
        ],
      }
      // if flashcard_entryID is in the query params, add it to params
      if (route.query.flashcard_entryID) {
        params.flashcard_entryID = route.query.flashcard_entryID
      }
      console.log("params", params)
      firstGame.value = false // Set firstGame to false after the first load
      url = `/flashcard/next-exercise/`
      console.log("posting to", url, params)
      response = await fetchWrapper.post(url, params)
    }
    else {
      const params = {
        native_language: profileStore.profile.native_language,
        learning_language: profileStore.profile.learning_language,
        enable_audio: enable_audio.value,
        enable_speaking: enable_speaking.value,
        mode: currentMode.value,
        force: firstGame.value,
      }
      const query = new URLSearchParams(params)
      url = `/curriculum/topics/${props.topicID}/next_exercise/?${query}`
      response = await fetchWrapper.get(url)
    }
    if (response.messages && reward_notifications) {
      console.log('response messages', response.messages)
      response.messages.forEach((message) => {
        reward_notifications.value.showMessage(message)
      })
    }
    if (response.error == 'No exercises available') {
      noExercisesAvailable.value = true
      nextSession.value = null
      showResult.value = false
      lastResult.value = null
      topicProgress.value = 1.0
      currentSession.value.remaining = 0
      emit('topic-completed')
      return
    }
    nextSession.value = response
    topicProgress.value = response.topic_progress // Update topicProgress
    currentSession.value.total = response.total // Total number of exercises
    currentSession.value.remaining = response.remaining // Remaining exercises
    checkComplete()
  } catch (error) {
    gameError.value = 'Failed to load exercise'
    currentSession.value = null
  } finally {
    isLoading.value = false
  }
  if (route.query.showAward) {
    showEncouragement(t('curriculum.number-in-a-row', { number: 2}))
  }
}

function activateGameState() {
  if (!isLoading.value) {
    currentSession.value = nextSession.value
    gameError.value = null
    showResult.value = false
    lastResult.value = null
    reloadKey.value += 1 // Force re-render of game component
  }
  else {
    // wait for loading to finish
    console.log('Game state is still loading, retrying...')
    setTimeout(() => {
      activateGameState()
    }, 25) // Retry after 25ms
  }
}

function checkComplete() {
  if (topicProgress.value === 1.0 && !notifiedCompleted.value) {
    notifiedCompleted.value = true
    emit('topic-completed')
  }
}

async function handleSkipConcept() {
  try {
    const url = `/curriculum/topics/${props.topicID}/skip_concept/`
    const response = await fetchWrapper.post(url, {
      quiz_sessionID: currentQuizSessionID.value,
      session_id: currentSession.value.session_id,
      concept_id: currentSession.value.game_state.current_concept.id,
    })
    if (response.messages && reward_notifications) {
      console.log('response messages', response.messages)
      response.messages.forEach((message) => {
        reward_notifications.value.showMessage(message)
      })
    }
    currentSession.value = {
      ...currentSession.value,
      game_state: response.game_state
    }
    topicProgress.value = response.topic_progress
    checkComplete()
  } catch (error) {
    console.error('Failed to skip concept:', error)
  }
  await loadGameState()
  await activateGameState()
}

async function disableAudio() {
  enable_audio.value = false
  await loadGameState()
  await activateGameState()
}

async function toggleAudio(reload = false) {
  enable_audio.value = !enable_audio.value
  console.log('toggleAudio', enable_audio.value)
  if (reload) {
    await loadGameState()
    await activateGameState()
  }
}

async function handleGameAction(action) {
  // we set isLoading here so we don't have a race condition between when they choose the game action and we request the next exercise -- no matter what, we wait until the server responds to the next exercise hit
  isLoading.value = true
  try {
    if (!action.pending) {
      if (!action.auto_advance) { // user is skipping, it's not really a streak
        lastAction.value = action
        showResult.value = true
        hasChattedAfterAnswer.value = false // Reset chat state when new answer is submitted
        // Play appropriate sound
        const soundUrl = action.correct ? successMp3 : wrongMp3
        if (action.correct) {
          updateCorrectMessage()
          miniStreak.value += 1
          if (miniStreak.value >= 3) {
            showEncouragement(t('curriculum.number-in-a-row', { number: miniStreak.value}))
          }
        } else {
          updateIncorrectMessage()
          miniStreak.value = 0
          showEncouragementMessage.value = false
        }
        // console.log('play ', soundUrl)
        if (enable_audio.value) {
          audiomanager.setPlaybackSpeed(1)
          audiomanager.play(soundUrl)
        }
      }
    }
    let url = ''
    if (props.flashcards) {
      url = `/flashcard/submit-answer/`
    }
    else {
      url = `/curriculum/topics/${props.topicID}/submit_answer/`
    }
    const response = await fetchWrapper.post(url, {
      quiz_sessionID: currentQuizSessionID.value,
      session_id: currentSession.value.session_id,
      answer: action
    })
    if (response.messages && reward_notifications) {
      console.log('response messages', response.messages)
      response.messages.forEach((message) => {
        reward_notifications.value.showMessage(message)
      })
    }

    // Update game state and show result
    currentSession.value = {
      ...currentSession.value,
      game_state: response.game_state
    }
    if (!action.pending && !action.auto_advance) {
      lastResult.value = response.result
      // topicProgress.value = response.topic_progress
      // progressDetails.value = response.progress_details
    }
    if (action.auto_advance) {
      console.log('Auto-advancing to next problem')
      await loadGameState()
      await handleNextProblem()
    }
    else if (!action.pending) {
      console.log('Not auto-advancing, showing result')
      await loadGameState()
    }
  } catch (error) {
    console.error('Failed to submit answer:', error)
    gameError.value = 'Failed to submit answer'
  }
}

async function handleNextProblem() {
  await activateGameState()
  emit('incrementGameStepsShown')
}

async function handleChatWithTutor() {
  // Toggle chat modal visibility
  showChatModal.value = !showChatModal.value
  // Mark that user has chatted after answering (if result is showing and in registration mode)
  if (showResult.value && currentMode.value === 'registration') {
    hasChattedAfterAnswer.value = true
    hasEverChattedInRegistration.value = true // Mark that they've chatted at least once during registration
  }
}

async function handleExampleSentences() {
  // Toggle example sentences modal visibility
  console.log("currently showing example sentences modal", showExampleSentencesModal.value)
  showExampleSentencesModal.value = !showExampleSentencesModal.value
  console.log("now showing example sentences modal", showExampleSentencesModal.value)
}

async function practice_more() {
  console.log('Practice more button clicked')
  
  if (props.flashcards) {
    // In flashcards mode, create a new quiz session and update state locally
    try {
      const response = await fetchWrapper.post('/flashcard/create-quiz/', {
        review_ahead: true,
        max_cards: 100,
        native_language: profileStore.profile.native_language,
        learning_language: profileStore.profile.learning_language,
      });

      const quizSessionID = response.quiz_sessionID;

      // Update the quiz session ID reactively
      currentQuizSessionID.value = quizSessionID;
      
      // Reset state for new quiz session
      firstGame.value = true
      noExercisesAvailable.value = false
      notifiedCompleted.value = false
      showResult.value = false
      lastResult.value = null
      gameError.value = null
      miniStreak.value = 0
      
      // Reload game state with new quiz session
      await loadGameState()
      await activateGameState()
    } catch (error) {
      console.error('Error creating practice quiz:', error);
    }
  } else {
    // In curriculum mode, switch to practice mode and reload the game state
    console.log('Switching to practice mode')
    
    // Update the current mode to practice
    currentMode.value = 'practice'
    
    // Reset state for new mode
    firstGame.value = true
    noExercisesAvailable.value = false
    notifiedCompleted.value = false
    
    // Reload game state with practice mode
    await loadGameState()
    await activateGameState()
  }
}

// try ion modal
const modal = ref();
const modalSentences = ref();
const input = ref();

onMounted(async () => {
  await profileStore.fetchProfile(true)
  await loadGameState()
  await activateGameState()
})
</script>

<template>
  <div class="game-wrapper">
    <!-- Chat Modal -->
    <ion-modal ref="modal" :is-open="showChatModal" @didDismiss="showChatModal = false">
      <ion-header>
        <ion-toolbar>
          <ion-buttons slot="start">
            <ion-button @click="showChatModal = false"><i class="bi bi-x action-icon"></i></ion-button>
          </ion-buttons>
          <ion-title><i class="bi bi-chat-dots-fill"></i> {{ t('curriculum.chat-with-tutor') }}</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" fullscreen>
        <MiniChat :templateID="230" :game_sessionID="currentSession.session_id" @close="showChatModal = false" />
      </ion-content>
    </ion-modal>
    <!-- Example Sentences Modal -->
    <ion-modal ref="modalSentences" :is-open="showExampleSentencesModal" @didDismiss="showExampleSentencesModal = false">
      <ion-header>
        <ion-toolbar>
          <ion-buttons slot="start">
            <ion-button @click="showExampleSentencesModal = false"><i class="bi bi-x action-icon"></i></ion-button>
          </ion-buttons>
          <ion-title><i class="bi bi-file-earmark-text-fill"></i> {{ t('curriculum.example-sentences') }}</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" fullscreen>
        <ExampleSentences :game_sessionID="currentSession.session_id"/>
      </ion-content>
    </ion-modal>
    <!-- Report Concept Modal -->
    <ion-modal ref="reportModal" :is-open="showReportModal">
      <ion-header>
        <ion-toolbar>
          <ion-title>{{ t('report.report-problem') }}</ion-title>
          <ion-buttons slot="start">
            <ion-button @click="showReportModal = false"><i class="bi bi-x"></i></ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" fullscreen>
        <ReportConceptModal
          :conceptID="currentSession.game_state.current_concept.id"
        />
      </ion-content>
    </ion-modal>

    <div v-if="gameError" class="alert alert-danger">
      {{ gameError }}
    </div>

    <div class="page-container d-flex flex-column align-items-center" style="height: 100%">
      <!-- Progress Bar -->
      <div v-if="!hideProgress" class="progress-row mt-4 d-flex align-items-center" style="width: 90%">
        <router-link v-if="!compact" :to="{ name: 'UnitProgressView' }" class="back-button me-2">
          <i class="bi bi-box-arrow-left action-icon back-to-unit-progress"></i>
        </router-link>
        <div class="progress-container">
            <div class="progress-container position-relative">
            <progress :value="topicProgress" max="1" class="topic-progress"></progress>
            <div v-if="cardsRemaining" class="progress-text position-absolute w-100 text-center" style="top: -1px; left: 0; color: white; font-weight: bold;">
              {{cardsTotal - cardsRemaining}}/{{cardsTotal}}
            </div>
            </div>
        </div>
        <!-- Practice Mode Indicator -->
        <div v-if="currentMode === 'practice'" class="practice-mode-indicator me-2">
          <span class="badge bg-success">
            <i class="bi bi-arrow-repeat me-1"></i>
            {{ t('curriculum.practice-mode', 'Practice') }}
          </span>
        </div>
        <router-link v-if="props.topicID && !compact" :to="{ name: 'CurriculumTopicView', params: { topicID: props.topicID } }" class="ms-3">
          <i class="bi bi-card-list action-icon"></i>
        </router-link>
      </div>
      <div v-if="currentSession" class="instructions-row mb-1 text-start">
        <span v-if="currentSession.game_state.game_type === 'multiple-choice'">{{ t('curriculum.select-the-correct-translation') }}</span>
        <span v-else-if="currentSession.game_state.game_type === 'audio-choice'">{{ t('curriculum.select-the-correct-translation-for-the-word-you') }}</span>
        <span v-else-if="currentSession.game_state.game_type === 'matching'">{{ t('curriculum.select-the-matching-words') }}</span>
        <span v-else-if="currentSession.game_state.game_type === 'free-translation'">{{ t('curriculum.translate-the-text') }}</span>
        <span v-else-if="currentSession.game_state.game_type === 'free-translation-listening'">{{ t('curriculum.translate-the-audio') }}</span>
        <span v-else-if="currentSession.game_state.game_type === 'free-translation-writing'">{{ t('curriculum.write-the-answer') }}</span>
        <span v-else-if="currentSession.game_state.game_type === 'introduce-concept'">{{ t('curriculum.learn-something-new') }}</span>
        <span v-else-if="currentSession.game_state.game_type === 'sentence-builder'">{{ t('curriculum.sentence-builder') }}</span>
        <span v-else-if="currentSession.game_state.game_type === 'sentence-builder-audio'">{{ t('curriculum.sentence-builder-audio') }}</span>
        <span v-else>{{ t('curriculum.unknown-game') }}</span>
      </div>

      <!-- Game Components -->
      <div v-if="noExercisesAvailable" class="no-exercises-message text-center mt-5">
        <div class="alert alert-info p-4">
          <h4 class="mb-3" v-if="currentMode === 'practice'">{{ t('curriculum.no-more-practice-title', 'Practice session complete!') }}</h4>
          <h4 class="mb-3" v-else>{{ t('curriculum.no-more-words-title', 'No more words to learn!') }}</h4>
          <p class="mb-4" v-if="currentMode === 'practice'">
            {{ t('curriculum.no-more-practice-desc', 'You have completed this practice session. You can continue practicing or explore other topics.') }}
          </p>
          <p class="mb-4" v-else>
            {{ t('curriculum.no-more-words-desc', 'You have completed all the vocabulary in this unit. Explore more topics to keep learning!') }}
          </p>
          <div class="d-flex gap-3 justify-content-center flex-wrap">
            <button v-if="currentMode !== 'practice'" @click="practice_more()" class="btn btn-primary btn-primary-cs fs-5">
              {{ t('curriculum.practice-more', 'Practice More') }} <i class="ms-2 bi bi-arrow-repeat"></i>
            </button>
            <button v-if="currentMode === 'practice'" @click="practice_more()" class="btn btn-primary btn-primary-cs fs-5">
              {{ t('curriculum.practice-again', 'Practice Again') }} <i class="ms-2 bi bi-arrow-repeat"></i>
            </button>
            <router-link :to="{ name: 'UnitProgressView' }" class="btn btn-primary btn-primary-cs fs-5">
              {{ t('curriculum.explore-more-vocabulary', 'Explore More Vocabulary') }} <i class="ms-2 bi bi-arrow-right-circle"></i>
            </router-link>
          </div>
        </div>
      </div>
      <div v-else-if="currentSession" class="game-container flex-grow-1" :key="reloadKey">
        <!-- Multiple Choice Game -->
        <MultipleChoiceGame
          v-if="currentSession.game_state.game_type === 'multiple-choice'"
          :game-state="currentSession.game_state"
          :compact="compact"
          :allowSkip="allowSkip"
          @toggle-audio="toggleAudio(false)"
          @game-action="handleGameAction"
          @chat-with-tutor="handleChatWithTutor"
          @example-sentences="handleExampleSentences"
          @skip-concept="handleSkipConcept"
          :enable_audio="enable_audio"
        />

        <!-- Audio Choice Game -->
        <AudioChoiceGame
          v-else-if="currentSession.game_state.game_type === 'audio-choice'"
          :game-state="currentSession.game_state"
          :compact="compact"
          @game-action="handleGameAction"
          @disable-audio="disableAudio"
          @chat-with-tutor="handleChatWithTutor"
          @example-sentences="handleExampleSentences"
        />

        <!-- Matching Game -->
        <MatchingGame
          v-else-if="currentSession.game_state.game_type === 'matching'"
          :game-state="currentSession.game_state"
          :compact="compact"
          @toggle-audio="toggleAudio(false)"
          @game-action="handleGameAction"
          @chat-with-tutor="handleChatWithTutor"
          @example-sentences="handleExampleSentences"
          :enable_audio="enable_audio"
        />

        <!-- Free Translation Game -->
        <FreeTranslationGame
          v-else-if="currentSession.game_state.game_type === 'free-translation' || currentSession.game_state.game_type === 'free-translation-listening' || currentSession.game_state.game_type === 'free-translation-writing'"
          :gameState="currentSession.game_state"
          :compact="compact"
          @game-action="handleGameAction"
          @toggle-audio="toggleAudio(false)"
          @reload="loadGameState"
          @chat-with-tutor="handleChatWithTutor"
          @example-sentences="handleExampleSentences"
          :enable_audio="enable_audio"
          :enable_speaking="enable_speaking"
        />

        <!-- Introduce Concept Game -->
        <IntroduceConceptGame
          v-else-if="currentSession?.game_state?.game_type === 'introduce-concept'"
          :game-state="currentSession.game_state"
          :compact="compact"
          :enable_audio="enable_audio"
          @game-action="handleGameAction"
          @toggle-audio="toggleAudio"
          @chat-with-tutor="handleChatWithTutor"
          @example-sentences="handleExampleSentences"
        />

        <!-- Sentence Builder Game -->
        <SentenceBuilderGame
          v-else-if="currentSession?.game_state?.game_type === 'sentence-builder'"
          :game-state="currentSession.game_state"
          :compact="compact"
          @game-action="handleGameAction"
          @toggle-audio="toggleAudio(false)"
        />

        <!-- Sentence Builder Audio Game -->
        <SentenceBuilderGame
          v-else-if="currentSession?.game_state?.game_type === 'sentence-builder-audio'"
          :game-state="currentSession.game_state"
          :compact="compact"
          @game-action="handleGameAction"
          @toggle-audio="toggleAudio(false)"
          @chat-with-tutor="handleChatWithTutor"
        />

        <!-- Add other game types here -->
        <div v-else class="alert alert-warning">
          Unsupported game type: {{ currentSession.game_state.game_type }}
        </div>
      </div>


      <!-- Result State -->
      <div v-if="showResult && currentSession" class="result-container position-relative">
        
        <!-- Tooltip for required chat - positioned outside result-message but anchored to chat icon -->
        <div v-if="shouldShowChatTooltip" class="chat-tooltip-external-anchored">
          <div class="tooltip-arrow-external-anchored"></div>
          <div class="tooltip-content">
            {{ t('curriculum.click-to-chat-with-tutor', 'Click here to chat with your tutor!') }}
          </div>
        </div>
        
        <div :class="['result-message rounded', compact ? 'compact-result-message' : '', lastAction?.correct ? 'success' : 'error']">
          <div class="d-flex justify-content-between">
            <div class="float-start text-result">
              <div v-if="lastAction?.correct" class="confetti confetti-1"></div>
              <div v-if="lastAction?.correct" class="confetti confetti-2"></div>
              <div v-if="lastAction?.correct" class="confetti confetti-3"></div>
              <div v-if="lastAction?.correct" class="confetti confetti-4"></div>
              <i v-if="lastAction?.correct" class="bi bi-check-circle-fill me-3"></i>
              <i v-else class="bi bi-x-circle-fill me-3"></i>
              {{ lastAction?.correct ? correctMessage : incorrectMessage }}
            </div>
            <div class="d-flex justify-content-end">
              <div class="float-end me-2" v-if="currentSession.game_state.current_concept?.id && !compact">
                <i class="bi bi-flag chat-icon" @click="showReportModal = true"></i>
              </div>
              <div :class="['float-end', 'text-white', 'chat-icon-target']">
                <i class="bi bi-chat-dots-fill me-2 chat-icon" @click="handleChatWithTutor"></i>
              </div>
            </div>
          </div>
          <!--
          <div v-if="showEncouragementMessage && currentSession && !isLoading" class="multicorrectnumbers">
            <span class="badge badge-cs rounded-pill p-2 px-3 fw-semibold shadow-sm fs-6">{{ showEncouragementMessage }}</span>
          </div>
          -->
          <!--
          <div v-if="topicProgress == 1 && lastAction?.correct" class="text-white finalMessage">
            <span class="badge badge-cs rounded-pill p-2 px-3 fw-semibold shadow-sm fs-6">
              <router-link :to="{ name: 'UnitProgressView' }" class="text-primary-cs  text-decoration-none">
                {{ t('game.unit-completed') }} <i class="ms-2 bi bi-arrow-right-circle"></i>
              </router-link>
            </span>
          </div>
          -->
          <p v-if="lastResult?.message">{{ lastResult?.message }}</p><!-- tani what is this for-->
          
        </div>

        <div :class="['button-container', compact ? 'compact-button-container' : '']">
          <button type="button"
            :class="['btn', 'fs-5', 'btn-primary btn-primary-cs']"
            @click="handleNextProblem"
          >
            <span>
              {{ t('cards.next') }} <i class="ms-2 bi bi-arrow-right-circle"></i>
            </span>
          </button>
        </div>

        <div v-if="showResult && isSuperuser">
          <h2>{{ t('curriculum.debug-info') }}</h2>
          <table class="table">
            <thead>
              <tr>
                <th>{{ t('curriculum.concept') }}</th>
                <th>{{ t('curriculum.in-progress') }}</th>
                <th>{{ t('curriculum.mastered') }}</th>
                <th>{{ t('curriculum.accuracy') }}</th>
                <th>{{ t('curriculum.attempts') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in progressDetails" :key="row.concept">
                <td>{{ row.concept }}</td>
                <td>{{ row.in_progress }}</td>
                <td>{{ row.is_mastered }}</td>
                <td>{{ row.accuracy }}</td>
                <td>{{ row.attempts }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div class="flashcardTip tip mt-2" v-if="!hideProgress && !compact">
        <p class="mb-0">{{ t('game.tip:-youll-earn-points-and-level-up-as') }}</p>
      </div>
    </div>
    <!--
    <button @click="showEncouragement('YOU GOT 5 IN A ROW!')">just for testing</button>
    -->

    <!-- Encouragement Message -->
    <div v-if="showEncouragementMessage && currentSession" :class="['encouragement-message', encouragementClass, 'multicolor-text']">
      <b>{{ showEncouragementMessage }}</b>
    </div>

  </div>
</template>

<style scoped>
#chatModal {
  top: 0px !important;
  height: 90%;
}
.chat-modal-body {
  height: 80vh;
}
.example-sentences-modal-body {
  height: 80vh;
}
.game-wrapper {
  position: relative;
  height: 100%;
}
.page-container {
  width: 100%;
  height: 100%;
}
.game-container {
  flex: 1;
  width: 100%;
}
.btn-secondary-cs-nw{
  color:#242f51 !important;
  background-color: #f1f5f9 !important;
  font-weight:500;
}
.btn-secondary-cs-nw:hover{
  color:#ffffff !important;
  background-color: #242f51 !important;
}
.flashcardTip p{
  font-size: 14px;
  color: #959595;
}
progress {
  width: 100%;
  height: 20px;
  margin-bottom: 1rem;
}

.result-container {
  text-align: center;
  padding-top: 0rem;
  padding-bottom: 0rem;
  max-width: 600px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

}

.compact-button-container button {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}

.button-container {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
}

.btn-primary {
  background-color: #007bff;
  color: white;
  border: none;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
}

.topic-progress {
  height: 20px;
  flex-grow: 1;
  -webkit-appearance: none;
  appearance: none;
  border-radius: 10px;
}

.progress-container {
  width: 100%;
  height: 20px;
  border-radius: 10px; /* Rounded corners */
  background-color: black; /* Uncompleted portion */
  overflow: hidden; /* Clip inner progress */
}

.instructions-row {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  font-size: 80%;
}

.progress-row {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

progress {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 100%;
  border-radius: 10px;
}

progress::-webkit-progress-value {
  background-color:  var(--primary-color);
}

progress::-webkit-progress-bar {
  background-color: #e0e0e0;
}

progress::-moz-progress-bar {
  background-color:  var(--primary-color)
}
.topic-progress[value] {
  height: 20px;
  border-radius: 10px;
  flex-grow: 1;
  -webkit-appearance: none;
  appearance: none;
  /* background-color: red; */
}

.back-to-unit-progress {
  font-size: 1.7rem;
  color: #193b5c !important;
}
.bi-info-circle {
  font-size: 2rem;
}

.custom-modal {
  height: 80vh; /* 80% of the viewport height */
}

@media (max-width: 576px) {
  .btn-xs-short {
    padding: 0.25rem 0.5rem !important;
    font-size: 0.8rem !important;
  }
  .btn-xs {
    padding: 0.5rem 1rem !important;
    font-size: 0.9rem !important;
  }
}
.rounded {
  border-radius: 1rem;
}

.encouragement-message {
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  color: black;
  padding: 10px 0px;
  border-radius: 5px;
  z-index: 1000;
  font-size: 1.2rem;
  opacity: 0;
  transition: opacity 1s;
}

.encouragement-message.fade-in {
  opacity: 1;
}

.encouragement-message.fade-out {
  opacity: 0;
}

.multicolor-text {
  background: linear-gradient(45deg, #ff0000, #ff7f00, #ff8400, #00ff00, #0000ff, #4b0082, #8b00ff);
  background-color: grey;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradient-animation 3s infinite;
}

.text-result {
  font-size: 20px;
  font-weight: 600;
}

@keyframes gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
.tip {
  bottom: env(safe-area-inset-bottom);
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
}


.compact-result-message {
  margin: 5px auto !important;
  padding: 5px !important;
}

.result-message {
  margin: 10px auto;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

/* Success message styling - the fancy "Way to go!" */
.result-message.success {
  background: linear-gradient(145deg, #42b883, #3b9c6f);
  color: white;
  border: none;
  box-shadow: 0 10px 25px rgba(66, 184, 131, 0.3), 
              0 6px 12px rgba(0, 0, 0, 0.1),
              inset 0 -4px 0 rgba(0, 0, 0, 0.1);
  transform-origin: center bottom;
  animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

/* Error message styling */
.result-message.error {
  background: linear-gradient(145deg, #e74c3c, #c0392b);
  color: white;
  border: none;
  box-shadow: 0 10px 25px rgba(231, 76, 60, 0.3), 
              0 6px 12px rgba(0, 0, 0, 0.1),
              inset 0 -4px 0 rgba(0, 0, 0, 0.1);
  animation: shakeError 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) forwards;
}

.text-result {
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  text-shadow: 0 2px 2px rgba(0, 0, 0, 0.1);
}

/* Icon styling */
.bi-check-circle-fill, .bi-x-circle-fill {
  font-size: 1.8rem;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
  animation: iconPop 0.4s 0.1s ease-out forwards;
  transform: scale(0);
  opacity: 0;
}

.bi-flag {
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s ease;
  font-size: 1.2rem;
}

.bi-flag:hover {
  opacity: 1;
  transform: scale(1.1);
}

/* Shine effect for success message */
.result-message.success::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg, 
    rgba(255,255,255,0) 0%, 
    rgba(255,255,255,0.4) 50%, 
    rgba(255,255,255,0) 100%
  );
  animation: shine 1.5s 0.5s infinite;
  pointer-events: none;
}

.confetti {
    position: absolute;
    width: 10px;
    height: 10px;
    opacity: 0;
}

.confetti-1 {
    background-color: #FFC107;
    top: -20px;
    left: 20%;
    animation: confettiDrop 1s 0.1s ease-out infinite;
}

.confetti-2 {
    background-color: #FF5722;
    top: -20px;
    left: 40%;
    animation: confettiDrop 1.2s 0.2s ease-out infinite;
}

.confetti-3 {
    background-color: #2196F3;
    top: -20px;
    left: 60%;
    animation: confettiDrop 0.9s 0.3s ease-out infinite;
}

.confetti-4 {
    background-color: #9C27B0;
    top: -20px;
    left: 80%;
    animation: confettiDrop 1.1s 0.4s ease-out infinite;
}
.badge-cs{
  border: 1px solid #dfdfdfb0;
  background-color: #fff;
  color:#3b9c6f;
  font-size: 12px!important;
}
/* Animations */
@keyframes popIn {
  0% { transform: scale(0.8); opacity: 0; }
  40% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes iconPop {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes shine {
  0% { left: -100%; }
  100% { left: 100%; }
}

@keyframes shakeError {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}
@keyframes confettiDrop {
    0% { 
        transform: translateY(0) rotate(0deg); 
        opacity: 1;
    }
    100% { 
        transform: translateY(100px) rotate(360deg); 
        opacity: 0;
    }
}
/* Additional styling for messages */
.result-message p {
  margin-top: 12px;
  font-size: 1rem;
  opacity: 0.9;
  animation: fadeIn 0.4s 0.3s forwards;
  opacity: 0;
}

.result-message p strong {
  font-weight: 700;
}

.result-message p a {
  color: inherit;
  text-decoration: underline;
  font-weight: 700;
}

@keyframes fadeIn {
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 0.9; transform: translateY(0); }
}

.practice-mode-indicator {
  animation: fadeIn 0.5s ease-in-out;
}

.practice-mode-indicator .badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.65rem;
}

.multicorrectnumbers, .finalMessage{
  position: absolute;
  right: 15px;
  top: 26px;
}
.multicorrectnumbers.compact, .finalMessage.compact{
  position: absolute;
  right: 5px;
  top: 4px;
}
/* Responsive adjustments */
@media (max-width: 768px) {
  .text-result {
    font-size: 1.3rem;
  }
  
  .bi-check-circle-fill, .bi-x-circle-fill {
    font-size: 1.5rem;
  }
}
.chat-icon {
  cursor: pointer;
  font-size: 1.5rem;
  transition: color 0.2s ease, transform 0.2s ease;
}

/* Chat tooltip styles */
.chat-tooltip {
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 10px;
  background-color: #2c3e50;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  white-space: nowrap;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: tooltipPulse 2s infinite ease-in-out;
  pointer-events: none;
  display: block;
  visibility: visible;
  opacity: 1;
}

/* External tooltip that won't be clipped but anchored to chat icon */
.chat-tooltip-external-anchored {
  position: absolute;
  top: -20px;
  right: 4px;
  background-color: #2c3e50;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  white-space: nowrap;
  z-index: 10000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: tooltipPulse 2s infinite ease-in-out;
  pointer-events: none;
  display: block;
  visibility: visible;
  opacity: 1;
}

/* Chat icon target for positioning reference */
.chat-icon-target {
  position: relative;
}

/* Anchored tooltip positioned relative to chat icon */
.chat-tooltip-anchored {
  position: absolute;
  bottom: 130%;
  right: -10px;
  background-color: #2c3e50;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  white-space: nowrap;
  z-index: 10000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: tooltipPulse 2s infinite ease-in-out;
  pointer-events: none;
  display: block;
  visibility: visible;
  opacity: 1;
}

/* Chat icon container to ensure proper positioning */
.chat-icon-container {
  position: relative;
  z-index: 10001;
}

.tooltip-arrow {
  position: absolute;
  top: 100%;
  right: 15px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #2c3e50;
  z-index: 9999;
}

.tooltip-arrow-external {
  position: absolute;
  bottom: -6px;
  right: 20px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #2c3e50;
  z-index: 9999;
}

.tooltip-arrow-external-anchored {
  position: absolute;
  bottom: -6px;
  right: 15px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #2c3e50;
  z-index: 10000;
}

.tooltip-arrow-anchored {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #2c3e50;
  z-index: 10090;
}

.tooltip-content {
  font-weight: 500;
}

@keyframes tooltipPulse {
  0%, 100% { 
    opacity: 1; 
    transform: translateY(0);
  }
  50% { 
    opacity: 0.8; 
    transform: translateY(-2px);
  }
}
</style>
